import type { Member } from "@app/protocol";
import { Avatar } from "./Avatar";

export type ToastTone = "neutral" | "success" | "warning" | "error";

export interface ToastData {
  title: string;
  sub?: string | undefined;
  tone: ToastTone;
  member?: Member | null | undefined;
}

const EDGE: Record<ToastTone, string> = {
  neutral: "border-edge",
  success: "border-success/70",
  warning: "border-gold/70",
  error: "border-danger-soft/70",
};

const SIZE = {
  wide: { box: "px-5 py-2.5", title: "text-[16px]", sub: "text-[12.5px]", avatar: 38 },
  dense: { box: "px-3.5 py-1.5", title: "text-[14px]", sub: "text-[11px]", avatar: 28 },
} as const;

type Size = (typeof SIZE)[keyof typeof SIZE];

function Bubble({ toast, size }: { toast: ToastData; size: Size }) {
  return (
    <div
      className={`flex max-w-full animate-pop items-center gap-3 rounded-pill border-[1.5px] bg-panel-strong shadow-panel backdrop-blur-md ${size.box} ${EDGE[toast.tone]}`}
    >
      {toast.member ? <Avatar member={toast.member} size={size.avatar} /> : null}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className={`truncate font-extrabold leading-tight ${size.title}`}>{toast.title}</span>
        {toast.sub ? (
          <span className={`truncate font-semibold leading-tight text-ink/70 ${size.sub}`}>
            {toast.sub}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/** One message over the middle of the table (design: toast): hand results, events, errors. */
export function Toast({ toast, dense = false }: { toast: ToastData | null; dense?: boolean }) {
  return (
    <div aria-live="polite" className="pointer-events-none flex h-full items-center justify-center">
      {toast === null ? null : (
        <Bubble
          key={`${toast.title}|${toast.sub ?? ""}`}
          toast={toast}
          size={SIZE[dense ? "dense" : "wide"]}
        />
      )}
    </div>
  );
}
